/*
 * BRDG native quote form: progressive enhancement (pivot W6).
 *
 * Loaded by sections/brdg-quote-form.liquid only, as a deferred module. The
 * form works without it (a plain POST answered with a redirect). With it:
 *  - the ICA quote basket (`window.icaQuote.Store/Drawer`, storage key
 *    `ica_quote_basket_v1`) is read and shown, ported from the Canvas runtime
 *    (packages/runtime/src/theme-storefront.ts, `readIcaQuoteBasket` and the
 *    form node's basket panel);
 *  - fields are checked inline with the Canvas form's own messages;
 *  - the request is sent as JSON with a random idempotency key and a per-tab
 *    session token, and a retry resends the SAME frozen request.
 *
 * The server stays the authority: it composes the phone number with the
 * shared phone rule and validates everything against the approved form.
 */

export const BASKET_KEY = "ica_quote_basket_v1";
const SESSION_KEY = "brdg_form_session_v1";
const LINE_CONTROLS = new RegExp(
  "[\\u0000-\\u001f\\u007f\\u0085" + String.fromCharCode(0x2028, 0x2029) + "]",
);
// The shared email predicate (packages/contracts/src/storefront-email.ts).
const EMAIL =
  /^(?!\.)(?!.*\.\.)([A-Z0-9_'+.-]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i;

export const MESSAGES = {
  basketUnreadable:
    "Your quote products could not be read. Open Edit quote to review them before sending.",
  basketEmpty:
    "No products in your quote yet. You can send a request without products.",
  basketEditorLoading:
    "The quote editor is still loading. Try Edit quote again before sending.",
  basketRefused:
    "We could not confirm one or more products in your quote, so your request was not sent. Open Edit quote to remove them, then send again. You can describe those products in your request instead.",
  required: "This field is required.",
  contact:
    "Add an email address or a valid international phone number so the store can answer you.",
  processing: "Agree to the processing statement before sending this request.",
  emailInvalid: "Enter a valid email address.",
  emailControls:
    "Enter a valid email address without line breaks or control characters.",
  emailMarketing: "Add an email address to receive marketing email.",
  smsMarketing:
    "Add a valid international phone number to receive marketing messages.",
  phone:
    "Enter a valid phone number for the selected country code, or a complete international number such as +233 24 123 4567.",
  phoneInternational:
    "Enter a complete international phone number starting with + and its country code, for example +233 24 123 4567.",
  lineValue:
    "Use at most 200 characters without line breaks or control characters.",
  fieldRefused: "This answer could not be accepted. Check it and send again.",
  sending: "Sending your request…",
  received: (reference) => `Request received. Your reference is ${reference}.`,
  test: "Test request checked. Nothing was stored or sent.",
  unconfirmed:
    "We could not confirm your request. Your details and products are kept. Retry to check the same request safely.",
  rateLimited:
    "This store has received too many requests. Please try again shortly.",
  formChanged:
    "This form was updated. Reload the page, then send your request again.",
  refused:
    "This request could not be sent. Check your details and try again, or contact the store directly.",
  tooFast: "Please check your details, then send your request again.",
  correct:
    "Review your details and quote products, then send the corrected request.",
};

/** Refusal codes the accept path raises BEFORE anything is stored, so the
 * shopper may correct and resend (`isThemeFormValidationRefusal`). */
const CORRECTABLE = new Set([
  "form_email_invalid",
  "form_phone_invalid",
  "form_contact_name_invalid",
  "form_contact_company_invalid",
  "form_contact_first_name_invalid",
  "form_contact_last_name_invalid",
  "form_marketing_consent_undeclared",
  "form_processing_consent_required",
  "form_contact_required",
  "form_contact_undeclared",
  "form_email_marketing_contact_required",
  "form_sms_marketing_contact_required",
  "form_fields_invalid",
  "form_items_invalid",
  "form_item_unknown",
]);
const FIELD_REFUSALS = {
  form_phone_invalid: ["contact.phone", "phone"],
  form_email_invalid: ["contact.email", "emailInvalid"],
  form_email_marketing_contact_required: ["contact.email", "emailMarketing"],
  form_sms_marketing_contact_required: ["contact.phone", "smsMarketing"],
  form_contact_name_invalid: ["contact.name", "lineValue"],
  form_contact_company_invalid: ["contact.company", "lineValue"],
  form_contact_first_name_invalid: ["contact.firstName", "lineValue"],
  form_contact_last_name_invalid: ["contact.lastName", "lineValue"],
  form_processing_consent_required: ["consent.processing", "processing"],
  form_contact_required: ["contact.email", "contact"],
};

export function icaQuoteBridge(win) {
  return win ? win.icaQuote : undefined;
}

/**
 * Reads only the explicitly selected merchant basket. Titles and images are
 * display hints; the accept path still verifies every product against the
 * store's catalog. Any unreadable row fails the WHOLE basket, so a request
 * never silently sends fewer products than the shopper chose.
 *
 * A line-for-line port of `readIcaQuoteBasket` in theme-storefront.ts; the
 * parity suite runs both on the same baskets.
 */
export function readIcaQuoteBasket(win, policy) {
  const failed = { items: [], rows: [], error: MESSAGES.basketUnreadable };
  if (!win) return failed;
  try {
    const bridge = icaQuoteBridge(win);
    let raw;
    if (bridge && bridge.Store && typeof bridge.Store.getBasket === "function")
      raw = bridge.Store.getBasket();
    else {
      // The theme chooses local storage, then session storage when local is
      // unavailable. Reading does not create keys or alter the basket.
      try {
        raw = JSON.parse(win.localStorage.getItem(BASKET_KEY) ?? "[]");
      } catch {
        raw = JSON.parse(win.sessionStorage.getItem(BASKET_KEY) ?? "[]");
      }
    }
    if (!Array.isArray(raw) || raw.length > policy.maxItems) return failed;
    const items = [];
    const rows = [];
    const strings = (record, key, cap) =>
      typeof record[key] === "string" ? record[key].slice(0, cap) : "";
    for (const candidate of raw) {
      if (
        !candidate ||
        typeof candidate !== "object" ||
        Array.isArray(candidate) ||
        ![Object.prototype, null].includes(Object.getPrototypeOf(candidate))
      )
        return failed;
      const product = strings(candidate, "product_gid", 100);
      const variant = strings(candidate, "variant_gid", 100);
      const quantity = candidate.qty;
      const notes = strings(candidate, "notes", 2001);
      if (
        !/^gid:\/\/shopify\/Product\/[1-9][0-9]{0,19}$/.test(product) ||
        (candidate.variant_gid != null &&
          candidate.variant_gid !== "" &&
          !/^gid:\/\/shopify\/ProductVariant\/[1-9][0-9]{0,19}$/.test(
            variant,
          )) ||
        typeof quantity !== "number" ||
        !Number.isInteger(quantity) ||
        quantity < 1 ||
        quantity > 1000 ||
        notes.length > 2000 ||
        (!policy.allowNotes && notes)
      )
        return failed;
      items.push({
        productGid: product,
        ...(variant ? { variantGid: variant } : {}),
        quantity,
        ...(notes ? { notes } : {}),
      });
      let image = "";
      const imageInput = strings(candidate, "image_src", 2048);
      if (imageInput) {
        const url = new URL(imageInput, win.location.origin);
        if (
          (url.protocol === "https:" || url.protocol === "http:") &&
          !url.username &&
          !url.password &&
          (url.origin === win.location.origin ||
            url.hostname === "cdn.shopify.com")
        )
          image = url.href;
      }
      rows.push({
        title:
          strings(candidate, "title", 300) ||
          strings(candidate, "handle", 200) ||
          "Quote product",
        vendor: strings(candidate, "brand", 200),
        image,
        notes,
        quantity,
      });
    }
    return { items, rows, error: "" };
  } catch {
    return failed;
  }
}

/** The shared email rule: canonical lowercase value, or the message. */
export function checkEmail(input) {
  if (input === undefined || input === "") return { ok: true, value: null };
  if (input.length > 254 || LINE_CONTROLS.test(input))
    return { ok: false, message: MESSAGES.emailControls };
  const value = input.normalize("NFKC").trim().toLowerCase();
  if (!value) return { ok: true, value: null };
  return value.length <= 254 && EMAIL.test(value)
    ? { ok: true, value }
    : { ok: false, message: MESSAGES.emailInvalid };
}

/**
 * A browser-side shape check of the phone. It refuses only what the shared
 * rule certainly refuses (wrong characters, no country for a national number,
 * a digit count no number has); the server composes and validates the
 * canonical E.164 value with the same rule the Canvas form uses.
 */
export function checkPhone(typed, countryValue) {
  if (typed === undefined) return { ok: true, value: null };
  if (typed.length > 40 || LINE_CONTROLS.test(typed)) return { ok: false };
  const value = typed.normalize("NFKC").trim();
  if (!value) return { ok: true, value: null };
  const digits = value.replace(/[^0-9]/g, "").length;
  if (value.startsWith("+"))
    return /^\+[1-9][0-9 ().-]*$/.test(value) && digits >= 7 && digits <= 15
      ? { ok: true, value }
      : { ok: false };
  if (!countryValue) return { ok: false };
  return /^[0-9 ().-]+$/.test(value) && digits >= 4 && digits <= 15
    ? { ok: true, value }
    : { ok: false };
}

/** Leading zero bits of a digest. */
export function leadingZeroBits(bytes) {
  let bits = 0;
  for (const byte of bytes) {
    if (byte) return bits + Math.clz32(byte) - 24;
    bits += 8;
  }
  return bits;
}

/**
 * A small synchronous SHA-256 and the proof-of-work search over it. Kept
 * self-contained (no outer names) because the same source runs in a Web
 * Worker built from a Blob URL.
 */
function powEngine() {
  const K = Int32Array.from(
    "428a2f98 71374491 b5c0fbcf e9b5dba5 3956c25b 59f111f1 923f82a4 ab1c5ed5 d807aa98 12835b01 243185be 550c7dc3 72be5d74 80deb1fe 9bdc06a7 c19bf174 e49b69c1 efbe4786 0fc19dc6 240ca1cc 2de92c6f 4a7484aa 5cb0a9dc 76f988da 983e5152 a831c66d b00327c8 bf597fc7 c6e00bf3 d5a79147 06ca6351 14292967 27b70a85 2e1b2138 4d2c6dfc 53380d13 650a7354 766a0abb 81c2c92e 92722c85 a2bfe8a1 a81a664b c24b8b70 c76c51a3 d192e819 d6990624 f40e3585 106aa070 19a4c116 1e376c08 2748774c 34b0bcb5 391c0cb3 4ed8aa4a 5b9cca4f 682e6ff3 748f82ee 78a5636f 84c87814 8cc70208 90befffa a4506ceb bef9a3f7 c67178f2".split(
      " ",
    ),
    (hex) => parseInt(hex, 16),
  );
  const H0 = Int32Array.from(
    "6a09e667 bb67ae85 3c6ef372 a54ff53a 510e527f 9b05688c 1f83d9ab 5be0cd19".split(
      " ",
    ),
    (hex) => parseInt(hex, 16),
  );
  const w = new Int32Array(64);
  const rotr = (x, n) => (x >>> n) | (x << (32 - n));
  /** One 64-byte block of `bytes` at `at` into `h`. */
  const compress = (h, bytes, at) => {
    for (let i = 0; i < 16; i++, at += 4)
      w[i] =
        (bytes[at] << 24) |
        (bytes[at + 1] << 16) |
        (bytes[at + 2] << 8) |
        bytes[at + 3];
    for (let i = 16; i < 64; i++) {
      const x = w[i - 15];
      const y = w[i - 2];
      w[i] =
        (((rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3)) + w[i - 16]) | 0) +
        (((rotr(y, 17) ^ rotr(y, 19) ^ (y >>> 10)) + w[i - 7]) | 0);
    }
    let a = h[0],
      b = h[1],
      c = h[2],
      d = h[3],
      e = h[4],
      f = h[5],
      g = h[6],
      k = h[7];
    for (let i = 0; i < 64; i++) {
      const t1 =
        (k +
          (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) +
          ((e & f) ^ (~e & g)) +
          K[i] +
          w[i]) |
        0;
      const t2 =
        ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) +
          ((a & b) ^ (a & c) ^ (b & c))) |
        0;
      k = g;
      g = f;
      f = e;
      e = (d + t1) | 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) | 0;
    }
    h[0] += a;
    h[1] += b;
    h[2] += c;
    h[3] += d;
    h[4] += e;
    h[5] += f;
    h[6] += g;
    h[7] += k;
  };
  /** Pads `tail` (the bytes after the last full block) for a message of
   * `total` bytes and compresses it into `h`. */
  const finish = (h, buffer, length, total) => {
    buffer[length++] = 0x80;
    const blocks = length + 8 <= 64 ? 1 : 2;
    buffer.fill(0, length, blocks * 64 - 4);
    const bits = total * 8;
    const end = blocks * 64;
    buffer[end - 4] = bits >>> 24;
    buffer[end - 3] = bits >>> 16;
    buffer[end - 2] = bits >>> 8;
    buffer[end - 1] = bits;
    buffer[end - 5] = (bits / 2 ** 32) & 255;
    for (let block = 0; block < blocks; block++)
      compress(h, buffer, block * 64);
  };
  const sha256 = (bytes) => {
    const h = H0.slice();
    const full = bytes.length & ~63;
    for (let at = 0; at < full; at += 64) compress(h, bytes, at);
    const buffer = new Uint8Array(128);
    buffer.set(bytes.subarray(full));
    finish(h, buffer, bytes.length - full, bytes.length);
    const out = new Uint8Array(32);
    for (let i = 0; i < 8; i++)
      for (let j = 0; j < 4; j++) out[i * 4 + j] = h[i] >>> (24 - j * 8);
    return out;
  };
  /** The first `n` in [from, to) whose `SHA-256(prefix + n)` has `difficulty`
   * leading zero bits, or -1. The prefix's full blocks are hashed once. */
  const search = (prefix, difficulty, from, to) => {
    const full = prefix.length & ~63;
    const middle = H0.slice();
    for (let at = 0; at < full; at += 64) compress(middle, prefix, at);
    const tail = prefix.subarray(full);
    const buffer = new Uint8Array(128);
    const h = new Int32Array(8);
    buffer.set(tail);
    // The decimal digits of n, counted up in place (no string per attempt).
    const digits = Array.from(String(from), (digit) => digit.charCodeAt(0));
    for (let n = from; n < to; n++) {
      let length = tail.length;
      for (let i = 0; i < digits.length; i++) buffer[length++] = digits[i];
      h.set(middle);
      finish(h, buffer, length, prefix.length + digits.length);
      const zeros = h[0] === 0 ? 32 + Math.clz32(h[1]) : Math.clz32(h[0]);
      if (zeros >= difficulty) return n;
      let i = digits.length - 1;
      while (i >= 0 && digits[i] === 57) digits[i--] = 48;
      if (i < 0) digits.unshift(49);
      else digits[i]++;
    }
    return -1;
  };
  return { sha256, search };
}

const engine = powEngine();
/** SHA-256 of bytes, synchronously (for tests and the fallback). */
export const sha256 = engine.sha256;
let worker = null;
const waiting = new Map();
let asked = 0;
const dropWorker = () => {
  worker = null;
  for (const resolve of waiting.values()) resolve(null);
  waiting.clear();
};

/**
 * The capability's proof-of-work: the first decimal `n` whose
 * `SHA-256(challenge + ":" + n)` starts with `difficulty` zero bits.
 *
 * Solved in a dedicated Web Worker made from a Blob URL, so the page never
 * blocks. When a worker cannot be made (a strict CSP, an old browser), the
 * same search runs on the main thread in slices of at most 8 ms, yielding
 * between slices.
 */
export async function solveChallenge(win, challenge, difficulty, options = {}) {
  if (!(difficulty >= 0 && difficulty <= 24)) return null;
  const prefix = new TextEncoder().encode(`${challenge}:`);
  if (options.worker !== false && win.Worker && win.Blob && win.URL) {
    const found = await new Promise((resolve) => {
      try {
        if (!worker) {
          const source = `const e=(${powEngine})();onmessage=(m)=>postMessage({i:m.data.i,n:e.search(m.data.p,m.data.d,0,2**31)});`;
          worker = new win.Worker(
            win.URL.createObjectURL(
              new win.Blob([source], { type: "text/javascript" }),
            ),
          );
          worker.onmessage = (event) => {
            waiting.get(event.data.i)?.(event.data.n);
            waiting.delete(event.data.i);
          };
          worker.onerror = dropWorker;
        }
        const id = ++asked;
        waiting.set(id, resolve);
        worker.postMessage({ i: id, p: prefix, d: difficulty });
      } catch {
        dropWorker();
        resolve(null);
      }
    });
    if (found !== null) return found < 0 ? null : String(found);
  }
  const now = () => (win.performance ?? Date).now();
  // Yield between slices with a message (no timer clamp, so the page gets
  // its turn without the search idling), else a zero timeout.
  const channel = win.MessageChannel ? new win.MessageChannel() : null;
  const pause = () =>
    new Promise((resolve) => {
      if (channel) {
        channel.port1.onmessage = () => resolve();
        channel.port2.postMessage(0);
      } else win.setTimeout(resolve, 0);
    });
  const slice = options.sliceMs ?? 8;
  try {
    for (let from = 0; from < 2 ** 31; ) {
      const started = now();
      while (now() - started < slice && from < 2 ** 31) {
        const found = engine.search(prefix, difficulty, from, from + 256);
        if (found >= 0) return String(found);
        from += 256;
      }
      await pause();
    }
  } finally {
    channel?.port1.close();
  }
  return null;
}

function randomToken(win, bytes) {
  const data = new Uint8Array(bytes);
  win.crypto.getRandomValues(data);
  let text = "";
  for (const byte of data) text += String.fromCharCode(byte);
  return win
    .btoa(text)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/** A fresh key for every new request; a retry reuses the frozen one. */
export function newIdempotencyKey(win) {
  return `bq_${randomToken(win, 18)}`;
}

/** One opaque token per browser tab (session storage), never a cookie. */
export function tabSessionToken(win, memory) {
  try {
    const stored = win.sessionStorage.getItem(SESSION_KEY);
    if (stored && /^[A-Za-z0-9_-]{16,120}$/.test(stored)) return stored;
    const token = randomToken(win, 24);
    win.sessionStorage.setItem(SESSION_KEY, token);
    return token;
  } catch {
    if (!memory.token) memory.token = randomToken(win, 24);
    return memory.token;
  }
}

/**
 * The request the form's controls describe, shaped exactly as the server
 * shapes the same controls' no-JavaScript post (the parity suite compares
 * the two).
 */
export function collectPayload(form) {
  const contact = {};
  const consent = { processing: false };
  const fields = {};
  for (const element of form.querySelectorAll("[data-brdg-name]")) {
    const [group, key] = element.getAttribute("data-brdg-name").split(".");
    const type = element.getAttribute("data-brdg-type");
    if (group === "contact") {
      const raw = element.value ?? "";
      if (!raw.normalize("NFKC").trim() && !LINE_CONTROLS.test(raw)) continue;
      contact[key] = raw;
    } else if (group === "consent") consent[key] = element.checked === true;
    else if (type === "boolean") fields[key] = element.checked === true;
    else if (type === "list") {
      const lines = element.value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
      if (lines.length) fields[key] = lines;
    } else {
      const entered = element.value.trim();
      if (entered) fields[key] = entered;
    }
  }
  return { contact, consent, fields, items: [] };
}

function node(doc, tag, text, className) {
  const element = doc.createElement(tag);
  if (text !== undefined) element.textContent = text;
  if (className) element.className = className;
  return element;
}

/** The basket panel: reads, draws and keeps the shopper's quote current. */
export function mountBasket(root, win, doc, setStatus) {
  const panel = root.querySelector("[data-brdg-basket]");
  if (!panel) return null;
  const policy = {
    maxItems: Number(root.getAttribute("data-max-items")) || 50,
    allowNotes: root.getAttribute("data-allow-notes") === "true",
  };
  const list = panel.querySelector("[data-brdg-basket-rows]");
  const message = panel.querySelector("[data-brdg-basket-message]");
  const edit = panel.querySelector("[data-brdg-basket-edit]");
  const state = { items: [], error: "", snapshot: "", frozen: false };
  edit.hidden = false;
  edit.addEventListener("click", () => {
    const bridge = icaQuoteBridge(win);
    if (bridge && bridge.Drawer && typeof bridge.Drawer.open === "function")
      bridge.Drawer.open();
    else {
      state.error = MESSAGES.basketEditorLoading;
      setStatus(state.error);
    }
  });
  const refresh = () => {
    if (state.frozen) return;
    const result = readIcaQuoteBasket(win, policy);
    state.items = result.items;
    state.error = result.error;
    const snapshot = JSON.stringify(result);
    if (snapshot === state.snapshot) return;
    state.snapshot = snapshot;
    panel.removeAttribute("data-invalid");
    list.replaceChildren();
    message.textContent = result.error
      ? result.error
      : result.rows.length
        ? ""
        : MESSAGES.basketEmpty;
    message.hidden = !message.textContent;
    for (const row of result.rows) {
      const item = node(doc, "li", undefined, "brdg-qf__basket-row");
      if (row.image) {
        const image = node(doc, "img");
        image.src = row.image;
        image.alt = "";
        image.width = 56;
        image.height = 56;
        image.loading = "lazy";
        item.append(image);
      } else {
        const placeholder = node(doc, "span", "—", "brdg-qf__thumb");
        placeholder.setAttribute("aria-hidden", "true");
        item.append(placeholder);
      }
      const description = node(doc, "div");
      description.append(node(doc, "strong", row.title));
      if (row.vendor) description.append(node(doc, "small", row.vendor));
      if (row.notes) description.append(node(doc, "small", row.notes));
      item.append(description, node(doc, "span", `Qty ${row.quantity}`));
      list.append(item);
    }
  };
  // Store.on has no public unsubscribe. Its published listener list is used
  // ONLY to remove our own callback; nothing here edits the basket.
  let subscribed;
  const reconcile = () => {
    const bridge = icaQuoteBridge(win);
    const store = bridge && bridge.Store;
    if (
      store &&
      store !== subscribed &&
      typeof store.on === "function" &&
      Array.isArray(store.listeners)
    ) {
      if (subscribed && subscribed.listeners)
        subscribed.listeners = subscribed.listeners.filter(
          (listener) => listener !== refresh,
        );
      store.on(refresh);
      subscribed = store;
    }
    refresh();
  };
  reconcile();
  win.addEventListener("storage", reconcile);
  win.addEventListener("focus", reconcile);
  doc.addEventListener("visibilitychange", reconcile);
  doc.addEventListener("click", reconcile);
  return {
    state,
    refresh,
    freeze(frozen) {
      state.frozen = frozen;
      edit.disabled = frozen;
    },
    refuse() {
      panel.setAttribute("data-invalid", "true");
      message.hidden = false;
      message.textContent = MESSAGES.basketRefused;
      state.snapshot = "";
      edit.focus();
    },
    destroy() {
      if (subscribed && subscribed.listeners)
        subscribed.listeners = subscribed.listeners.filter(
          (listener) => listener !== refresh,
        );
      win.removeEventListener("storage", reconcile);
      win.removeEventListener("focus", reconcile);
      doc.removeEventListener("visibilitychange", reconcile);
      doc.removeEventListener("click", reconcile);
    },
  };
}

/** Enhances one server-rendered form root. Returns a small handle for tests. */
export function enhance(root, options = {}) {
  const doc = root.ownerDocument;
  const win = options.window ?? doc.defaultView;
  const send = options.fetch ?? ((...args) => win.fetch(...args));
  const memory = {};
  const form = root.querySelector("[data-brdg-form-element]");
  if (!form || root.hasAttribute("data-brdg-enhanced")) return null;
  root.setAttribute("data-brdg-enhanced", "");
  const fieldset = form.querySelector("fieldset");
  const status = form.querySelector("[data-brdg-status]");
  const submit = form.querySelector("[data-brdg-submit]");
  const country = form.querySelector("[data-brdg-phone-country]");
  // Matched by attribute value, never by a selector built from a name, so a
  // server-supplied field key can never be read as CSS.
  const read = (name) =>
    [...form.querySelectorAll("[data-brdg-name]")].find(
      (control) => control.getAttribute("data-brdg-name") === name,
    ) ?? null;
  const setStatus = (text) => {
    status.textContent = text;
  };
  const basket = root.hasAttribute("data-items-source")
    ? mountBasket(root, win, doc, setStatus)
    : null;
  let pending = null;
  // When the form was shown, and a server-issued session for this view. The
  // capability costs a small proof-of-work, solved off the submit path: once
  // the shopper first touches the form (or soon after load), again before it
  // ages out, and when the tab comes back. Submitting never refreshes it.
  const now = options.now ?? (() => Date.now());
  const later = options.setTimeout ?? ((fn, ms) => win.setTimeout(fn, ms));
  const timing = {
    start: 1500,
    refresh: 45 * 60 * 1000,
    stale: 55 * 60 * 1000,
    issueWait: 3100,
    ...options.timing,
  };
  const shownAt = now();
  const session = { token: "", at: 0, loading: null, started: false, timer: 0 };
  const sessionUrl = root.getAttribute("data-session-url");
  const definitionKey = root.getAttribute("data-definition-key");
  const ask = (query) =>
    send(`${sessionUrl}?form=${encodeURIComponent(definitionKey)}${query}`, {
      method: "GET",
      credentials: "same-origin",
    }).then((response) => (response.ok ? response.json() : null));
  const loadSession = () => {
    if (!sessionUrl || !definitionKey) return Promise.resolve();
    if (session.loading) return session.loading;
    session.started = true;
    session.loading = ask("")
      .then(async (offer) => {
        if (!offer || typeof offer.challenge !== "string") return null;
        const solution = await solveChallenge(
          win,
          offer.challenge,
          Number(offer.difficulty),
        );
        if (solution === null) return null;
        return ask(
          `&challenge=${encodeURIComponent(offer.challenge)}&solution=${solution}`,
        );
      })
      .then((body) => {
        if (!body || typeof body.token !== "string") throw new Error("none");
        session.token = body.token;
        session.at = now();
        win.clearTimeout(session.timer);
        session.timer = later(loadSession, timing.refresh);
      })
      .catch(() => {})
      .finally(() => {
        session.loading = null;
      });
    return session.loading;
  };
  const startSession = () => {
    if (!session.started) loadSession();
  };
  later(startSession, timing.start);
  form.addEventListener("focusin", startSession, { once: true });
  doc.addEventListener("visibilitychange", () => {
    if (
      doc.visibilityState === "visible" &&
      !session.loading &&
      (!session.token || now() - session.at > timing.refresh)
    )
      loadSession();
  });
  /** The capability to send, or "" to send unverified. Waits only for work
   * already under way, or, when the mount fetch failed or never ran, for the
   * one fetch it must now make; then keeps 3 s after issue so it is never
   * too fast. A capability that went stale is not refreshed here. */
  const capabilityForSubmit = async () => {
    if (session.loading) await session.loading;
    else if (!session.token) await loadSession();
    if (!session.token || now() - session.at > timing.stale) return "";
    const wait = session.at + timing.issueWait - now();
    if (wait > 0) await new Promise((resolve) => later(resolve, wait));
    return session.token;
  };
  // The JS path validates inline with the Canvas form's messages; the native
  // `required` attributes stay for the no-JS path and assistive technology.
  form.noValidate = true;

  const setFieldError = (name, message) => {
    const control = read(name);
    const error = control && doc.getElementById(`${control.id}-error`);
    if (!error) return;
    error.textContent = message ?? "";
    error.hidden = message === null;
    if (message === null) control.removeAttribute("aria-invalid");
    else control.setAttribute("aria-invalid", "true");
  };
  const phoneMessage = () =>
    country ? MESSAGES.phone : MESSAGES.phoneInternational;
  const phoneChecked = () => {
    const phone = read("contact.phone");
    return phone
      ? checkPhone(phone.value, country ? country.value : "")
      : { ok: true, value: null };
  };
  const emailChecked = () => {
    const email = read("contact.email");
    return email ? checkEmail(email.value) : { ok: true, value: null };
  };
  const problemOf = {
    "contact.email": () => {
      const checked = emailChecked();
      return checked.ok ? null : checked.message;
    },
    "contact.phone": () => (phoneChecked().ok ? null : phoneMessage()),
  };
  // Malformed contact feedback starts on blur; any correction clears as soon
  // as the value is acceptable, so half-typed values are not interrupted.
  for (const control of form.querySelectorAll("[data-brdg-name]")) {
    const name = control.getAttribute("data-brdg-name");
    const problem = problemOf[name];
    if (problem)
      control.addEventListener("blur", () => {
        if (!pending && control.value) setFieldError(name, problem());
      });
    const clear = () => {
      if (
        !pending &&
        control.getAttribute("aria-invalid") &&
        !(problem && problem())
      )
        setFieldError(name, null);
    };
    control.addEventListener("input", clear);
    control.addEventListener("change", clear);
  }
  if (country)
    country.addEventListener("change", () => {
      if (!pending && !problemOf["contact.phone"]())
        setFieldError("contact.phone", null);
    });

  /** Every problem the accept path would certainly refuse, in form order.
   * Returns the first control to fix, or null. */
  const validate = () => {
    const problems = [];
    for (const control of form.querySelectorAll("[data-brdg-name]")) {
      const name = control.getAttribute("data-brdg-name");
      setFieldError(name, null);
      const empty =
        control.type === "checkbox" ? !control.checked : !control.value.trim();
      if (control.required && empty)
        problems.push([
          name,
          name === "consent.processing"
            ? MESSAGES.processing
            : MESSAGES.required,
        ]);
      else if (problemOf[name] && problemOf[name]())
        problems.push([name, problemOf[name]()]);
    }
    const email = emailChecked();
    const phone = phoneChecked();
    if (email.ok && phone.ok && !email.value && !phone.value)
      problems.push([
        read("contact.email") ? "contact.email" : "contact.phone",
        MESSAGES.contact,
      ]);
    if (read("consent.emailMarketing")?.checked && email.ok && !email.value)
      problems.push(["contact.email", MESSAGES.emailMarketing]);
    if (read("consent.smsMarketing")?.checked && phone.ok && !phone.value)
      problems.push(["contact.phone", MESSAGES.smsMarketing]);
    const seen = new Set();
    for (const [name, message] of problems)
      if (!seen.has(name)) {
        seen.add(name);
        setFieldError(name, message);
      }
    const first = problems.find(([name]) => read(name));
    return first ? read(first[0]) : null;
  };

  const lock = (locked) => {
    fieldset.disabled = locked;
    submit.disabled = locked;
    if (basket) basket.freeze(locked);
  };
  const sendFrozen = async (request) => {
    pending = request;
    lock(true);
    setStatus(MESSAGES.sending);
    let response = null;
    let body = null;
    try {
      response = await send(form.action, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify(request),
      });
      body = await response.json().catch(() => null);
    } catch {
      response = null;
    }
    if (
      response &&
      body &&
      body.ok === true &&
      (response.status === 200 || response.status === 202)
    ) {
      root.setAttribute("data-brdg-state", body.test ? "tested" : "sent");
      setStatus(body.test ? MESSAGES.test : MESSAGES.received(body.reference));
      status.focus();
      return;
    }
    const code = body && typeof body.code === "string" ? body.code : "";
    if (
      response &&
      response.status === 400 &&
      body &&
      body.ok === false &&
      CORRECTABLE.has(code)
    ) {
      // Refused before anything was stored: the shopper may correct it.
      pending = null;
      lock(false);
      if (basket) basket.refresh();
      setStatus(MESSAGES.correct);
      if (code === "form_item_unknown" || code === "form_items_invalid") {
        if (basket) basket.refuse();
        setStatus(MESSAGES.basketRefused);
        return;
      }
      const field = FIELD_REFUSALS[code];
      if (field && read(field[0])) {
        setFieldError(
          field[0],
          field[1] === "phone" ? phoneMessage() : MESSAGES[field[1]],
        );
        read(field[0]).focus();
        return;
      }
      if (code === "form_fields_invalid" && Array.isArray(body.issues)) {
        let first = null;
        for (const issue of body.issues) {
          if (!issue || typeof issue.fieldKey !== "string") continue;
          const name = `field.${issue.fieldKey}`;
          if (!read(name)) continue;
          setFieldError(name, MESSAGES.fieldRefused);
          first = first || read(name);
        }
        if (first) first.focus();
      }
      return;
    }
    if (
      response &&
      response.status >= 400 &&
      response.status < 500 &&
      response.status !== 429 &&
      response.status !== 408
    ) {
      // A definitive refusal: nothing was accepted, so the shopper's answers
      // are theirs to change and the next send is a new request.
      pending = null;
      lock(false);
      if (basket) basket.refresh();
      setStatus(
        response.status === 409 || code === "form_not_active"
          ? MESSAGES.formChanged
          : code === "form_too_fast"
            ? MESSAGES.tooFast
            : MESSAGES.refused,
      );
      return;
    }
    // Anything else may already have been accepted. Keep the exact request
    // and its key, so a retry replays rather than duplicates.
    submit.disabled = false;
    setStatus(
      response && response.status === 429
        ? MESSAGES.rateLimited
        : MESSAGES.unconfirmed,
    );
  };

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (submit.disabled) return;
    if (pending) {
      await sendFrozen(pending);
      return;
    }
    if (basket) {
      basket.refresh();
      if (basket.state.error) {
        setStatus(basket.state.error);
        return;
      }
    }
    const invalid = validate();
    if (invalid) {
      setStatus("");
      invalid.focus();
      return;
    }
    const payload = collectPayload(form);
    // The displayed quote, captured at the same boundary as the fields.
    payload.items = basket
      ? basket.state.items.map((item) => ({ ...item }))
      : [];
    const value = (name) =>
      form.querySelector(`input[name="${name}"]`)?.value ?? "";
    const testLane = value("test_lane");
    const trap = value("brdg_website");
    // Any wait for the capability shows the normal sending state.
    lock(true);
    setStatus(MESSAGES.sending);
    const capability = await capabilityForSubmit();
    await sendFrozen({
      version: "brdg-native-form-submission/1",
      definitionKey: value("definition_key"),
      definitionHash: value("definition_hash"),
      installationRevision: Number(value("installation_revision")),
      idempotencyKey: newIdempotencyKey(win),
      sessionToken: tabSessionToken(win, memory),
      occurredAt: new Date().toISOString(),
      ...(country ? { phoneCountry: country.value } : {}),
      ...(testLane ? { testLane } : {}),
      ...(capability
        ? { sessionCapability: capability }
        : { startedAt: shownAt }),
      ...(trap ? { trap } : {}),
      payload,
    });
  });
  return { form, basket, validate };
}

export function enhanceAll(scope, options) {
  return [...scope.querySelectorAll("[data-brdg-form]")]
    .map((root) => enhance(root, options))
    .filter(Boolean);
}

const page = globalThis.document;
if (page && !globalThis.__BRDG_FORMS_MANUAL__) {
  if (page.readyState === "loading")
    page.addEventListener("DOMContentLoaded", () => enhanceAll(page));
  else enhanceAll(page);
  // The theme editor re-renders a section in place.
  page.addEventListener("shopify:section:load", (event) =>
    enhanceAll(event.target?.querySelectorAll ? event.target : page),
  );
}
